package br.com.htms.inspeccaocampo.shared.ui.components

import br.com.htms.inspeccaocampo.ui.theme.PortalCores
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DatePicker
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextFieldColors
import androidx.compose.material3.TimePicker
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.formatDateBr
import br.com.htms.inspeccaocampo.platform.yearOf

/**
 * Campo de data/validade no mesmo visual do formulário ([AppOutlinedTextField]).
 *
 * Os dígitos ficam “crus” no TextField e a máscara é só visual — evita o cursor
 * pular e um dígito “trocar” o outro ao digitar (comum no Web/mobile).
 */
enum class DateFieldKind(
    val maxDigits: Int,
    val placeholder: String
) {
    /** dd/MM/yyyy */
    DATE(8, "dd/mm/aaaa"),

    /** MM/yyyy */
    MONTH_YEAR(6, "mm/aaaa"),

    /** aaaa (1950–ano atual + 1) */
    YEAR(4, "aaaa"),

    /** dd/MM/yyyy HH:mm */
    DATE_TIME(12, "dd/mm/aaaa hh:mm"),

    /** HH:mm */
    TIME(4, "hh:mm")
}

object DateInputMask {
    fun digitsOnly(raw: String, kind: DateFieldKind): String =
        raw.filter { it.isDigit() }.take(kind.maxDigits)

    fun format(raw: String, kind: DateFieldKind): String =
        formatFromDigits(digitsOnly(raw, kind), kind)

    fun formatFromDigits(digits: String, kind: DateFieldKind): String {
        val d = digits.filter { it.isDigit() }.take(kind.maxDigits)
        return when (kind) {
            DateFieldKind.DATE -> buildString {
                d.forEachIndexed { i, c ->
                    if (i == 2 || i == 4) append('/')
                    append(c)
                }
            }
            DateFieldKind.MONTH_YEAR -> buildString {
                d.forEachIndexed { i, c ->
                    if (i == 2) append('/')
                    append(c)
                }
            }
            DateFieldKind.YEAR -> d
            DateFieldKind.DATE_TIME -> buildString {
                d.forEachIndexed { i, c ->
                    when (i) {
                        2, 4 -> append('/')
                        8 -> append(' ')
                        10 -> append(':')
                    }
                    append(c)
                }
            }
            DateFieldKind.TIME -> buildString {
                d.forEachIndexed { i, c ->
                    if (i == 2) append(':')
                    append(c)
                }
            }
        }
    }

    /** Índices no texto formatado onde há separador (antes do dígito i). */
    fun separatorBeforeDigit(kind: DateFieldKind, digitIndex: Int): Int =
        when (kind) {
            DateFieldKind.DATE -> when {
                digitIndex >= 4 -> 2
                digitIndex >= 2 -> 1
                else -> 0
            }
            DateFieldKind.MONTH_YEAR -> if (digitIndex >= 2) 1 else 0
            DateFieldKind.YEAR -> 0
            DateFieldKind.DATE_TIME -> when {
                digitIndex >= 10 -> 4
                digitIndex >= 8 -> 3
                digitIndex >= 4 -> 2
                digitIndex >= 2 -> 1
                else -> 0
            }
            DateFieldKind.TIME -> if (digitIndex >= 2) 1 else 0
        }

    fun now(kind: DateFieldKind): String {
        val full = formatDateBr(currentTimeMillis()) // dd/MM/yyyy HH:mm
        return when (kind) {
            DateFieldKind.DATE -> full.substringBefore(' ').ifBlank { full.take(10) }
            DateFieldKind.DATE_TIME -> full
            DateFieldKind.TIME -> full.substringAfter(' ', missingDelimiterValue = "").ifBlank {
                full.takeLast(5)
            }
            DateFieldKind.MONTH_YEAR -> {
                val date = full.substringBefore(' ')
                val parts = date.split('/')
                if (parts.size >= 3) "${parts[1]}/${parts[2]}" else format(full, kind)
            }
            DateFieldKind.YEAR -> yearOf(currentTimeMillis()).toString()
        }
    }

    fun quickLabel(kind: DateFieldKind): String = when (kind) {
        DateFieldKind.DATE -> "Hoje"
        DateFieldKind.MONTH_YEAR -> "Mês atual"
        DateFieldKind.YEAR -> "Este ano"
        DateFieldKind.DATE_TIME, DateFieldKind.TIME -> "Agora"
    }

    /** Aceita só dígitos que formam data/hora/ano reais (rejeita 99/98/8888). */
    fun constrainDigits(digits: String, kind: DateFieldKind): String {
        val raw = digits.filter { it.isDigit() }.take(kind.maxDigits)
        var acc = ""
        for (c in raw) {
            val trial = acc + c
            if (!isPossible(trial, kind)) break
            acc = trial
        }
        return acc
    }

    fun isCompleteValid(value: String, kind: DateFieldKind): Boolean {
        val d = digitsOnly(value, kind)
        return d.length == kind.maxDigits && isPossible(d, kind)
    }

    fun isBlankOrCompleteValid(value: String, kind: DateFieldKind): Boolean {
        val d = digitsOnly(value, kind)
        if (d.isEmpty()) return true
        return isCompleteValid(value, kind)
    }

    fun mensagemSeInvalido(value: String, kind: DateFieldKind, rotulo: String): String? {
        val d = digitsOnly(value, kind)
        if (d.isEmpty()) return null
        if (d.length < kind.maxDigits) {
            return when (kind) {
                DateFieldKind.YEAR -> "Informe $rotulo com 4 dígitos (${kind.placeholder})."
                else -> "Informe $rotulo completa (${kind.placeholder})."
            }
        }
        if (!isCompleteValid(value, kind)) {
            return when (kind) {
                DateFieldKind.YEAR -> "$rotulo inválido."
                else -> "$rotulo inválida."
            }
        }
        return null
    }

    private fun isPossible(digits: String, kind: DateFieldKind): Boolean =
        when (kind) {
            DateFieldKind.DATE -> possibleDate(digits)
            DateFieldKind.MONTH_YEAR -> possibleMonthYear(digits)
            DateFieldKind.YEAR -> possibleYear(digits)
            DateFieldKind.TIME -> possibleTime(digits)
            DateFieldKind.DATE_TIME -> {
                val date = digits.take(8)
                val time = digits.drop(8)
                possibleDate(date) && (time.isEmpty() || possibleTime(time))
            }
        }

    private fun possibleDate(digits: String): Boolean {
        if (digits.isEmpty()) return true
        val dayTens = digits[0].digitToInt()
        if (dayTens !in 0..3) return false
        if (digits.length == 1) return true
        val day = digits.take(2).toInt()
        if (day !in 1..31) return false
        if (digits.length == 2) return true
        val monthTens = digits[2].digitToInt()
        if (monthTens !in 0..1) return false
        if (digits.length == 3) return true
        val month = digits.substring(2, 4).toInt()
        if (month !in 1..12) return false
        if (day > daysInMonth(month, 2024)) return false
        if (digits.length == 4) return true
        if (digits[4] !in '1'..'2') return false
        if (digits.length == 5) return true
        val yearPrefix = digits.substring(4, 6).toInt()
        if (yearPrefix !in 19..21) return false
        if (digits.length < 8) return true
        val year = digits.substring(4, 8).toInt()
        if (year !in 1900..2100) return false
        return day in 1..daysInMonth(month, year)
    }

    private fun possibleMonthYear(digits: String): Boolean {
        if (digits.isEmpty()) return true
        if (digits[0].digitToInt() !in 0..1) return false
        if (digits.length == 1) return true
        val month = digits.take(2).toInt()
        if (month !in 1..12) return false
        if (digits.length == 2) return true
        return possibleYear(digits.drop(2))
    }

    private fun possibleYear(digits: String): Boolean {
        if (digits.isEmpty()) return true
        if (digits[0] !in '1'..'2') return false
        if (digits.length == 1) return true
        val prefix = digits.take(2).toInt()
        if (prefix !in 19..20) return false
        if (digits.length < 4) return true
        val year = digits.take(4).toInt()
        val maxYear = yearOf(currentTimeMillis()) + 1
        return year in 1950..maxYear
    }

    private fun possibleTime(digits: String): Boolean {
        if (digits.isEmpty()) return true
        if (digits[0].digitToInt() !in 0..2) return false
        if (digits.length == 1) return true
        val hour = digits.take(2).toInt()
        if (hour !in 0..23) return false
        if (digits.length == 2) return true
        if (digits[2].digitToInt() !in 0..5) return false
        if (digits.length == 3) return true
        val minute = digits.substring(2, 4).toInt()
        return minute in 0..59
    }

    private fun daysInMonth(month: Int, year: Int): Int = when (month) {
        1, 3, 5, 7, 8, 10, 12 -> 31
        4, 6, 9, 11 -> 30
        2 -> if (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) 29 else 28
        else -> 0
    }

    fun pickerLabel(kind: DateFieldKind): String = when (kind) {
        DateFieldKind.TIME -> "Hora"
        else -> "Calendário"
    }

    fun yearRange(kind: DateFieldKind): IntRange {
        val maxYear = yearOf(currentTimeMillis()) + 1
        return when (kind) {
            DateFieldKind.YEAR -> 1950..maxYear
            else -> 1950..2100
        }
    }

    fun todayUtcMillis(): Long {
        val date = formatDateBr(currentTimeMillis()).substringBefore(' ')
        val parts = date.split('/')
        if (parts.size < 3) return 0L
        val day = parts[0].toIntOrNull() ?: 1
        val month = parts[1].toIntOrNull() ?: 1
        val year = parts[2].toIntOrNull() ?: yearOf(currentTimeMillis())
        return utcMillis(year, month, day)
    }

    fun toUtcMillis(value: String, kind: DateFieldKind): Long? {
        if (kind == DateFieldKind.TIME) return null
        val checkKind = if (kind == DateFieldKind.DATE_TIME) DateFieldKind.DATE else kind
        val d = if (kind == DateFieldKind.DATE_TIME) {
            digitsOnly(value, DateFieldKind.DATE_TIME).take(8)
        } else {
            digitsOnly(value, checkKind)
        }
        if (d.length < checkKind.maxDigits || !isPossible(d, checkKind)) return null
        return when (checkKind) {
            DateFieldKind.DATE -> utcMillis(
                d.substring(4, 8).toInt(),
                d.substring(2, 4).toInt(),
                d.substring(0, 2).toInt()
            )
            DateFieldKind.MONTH_YEAR -> utcMillis(
                d.substring(2, 6).toInt(),
                d.substring(0, 2).toInt(),
                1
            )
            DateFieldKind.YEAR -> utcMillis(d.take(4).toInt(), 1, 1)
            DateFieldKind.DATE_TIME, DateFieldKind.TIME -> null
        }
    }

    fun formatFromUtcMillis(ms: Long, kind: DateFieldKind): String {
        val (year, month, day) = civilFromUtcMillis(ms)
        val digits = when (kind) {
            DateFieldKind.DATE, DateFieldKind.DATE_TIME ->
                pad2(day) + pad2(month) + year.toString().padStart(4, '0')
            DateFieldKind.MONTH_YEAR ->
                pad2(month) + year.toString().padStart(4, '0')
            DateFieldKind.YEAR ->
                year.toString().padStart(4, '0')
            DateFieldKind.TIME -> return format(now(kind), kind)
        }
        val asKind = if (kind == DateFieldKind.DATE_TIME) DateFieldKind.DATE else kind
        return formatFromDigits(constrainDigits(digits, asKind), asKind)
    }

    fun parseHourMinute(value: String): Pair<Int, Int> {
        val d = digitsOnly(value, DateFieldKind.TIME)
        val nowDigits = digitsOnly(now(DateFieldKind.TIME), DateFieldKind.TIME)
        val hour = d.take(2).toIntOrNull()
            ?: nowDigits.take(2).toIntOrNull()
            ?: 0
        val minute = d.drop(2).take(2).toIntOrNull()
            ?: nowDigits.drop(2).take(2).toIntOrNull()
            ?: 0
        return hour.coerceIn(0, 23) to minute.coerceIn(0, 59)
    }

    fun formatHourMinute(hour: Int, minute: Int): String =
        formatFromDigits(pad2(hour.coerceIn(0, 23)) + pad2(minute.coerceIn(0, 59)), DateFieldKind.TIME)

    private fun pad2(n: Int): String = n.toString().padStart(2, '0')

    /** Meia-noite UTC de yyyy-MM-dd — o DatePicker do Material3 usa esse epoch. */
    fun utcMillis(year: Int, month: Int, day: Int): Long =
        daysFromCivil(year, month, day) * 86_400_000L

    fun civilFromUtcMillis(ms: Long): Triple<Int, Int, Int> =
        civilFromEpochDay(floorDiv(ms, 86_400_000L))

    private fun floorDiv(a: Long, b: Long): Long {
        var q = a / b
        val r = a % b
        if (r != 0L && (a < 0) != (b < 0)) q--
        return q
    }

    /** Algoritmo de Howard Hinnant: dias civis desde 1970-01-01. */
    private fun daysFromCivil(year: Int, month: Int, day: Int): Long {
        var y = year.toLong()
        val m = month.toLong()
        val d = day.toLong()
        y -= if (m <= 2) 1 else 0
        val era = (if (y >= 0) y else y - 399) / 400
        val yoe = y - era * 400
        val doy = (153 * (m + if (m > 2) -3 else 9) + 2) / 5 + d - 1
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
        return era * 146097 + doe - 719468
    }

    private fun civilFromEpochDay(z0: Long): Triple<Int, Int, Int> {
        val z = z0 + 719468
        val era = (if (z >= 0) z else z - 146096) / 146097
        val doe = z - era * 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        var y = yoe + era * 400
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = doy - (153 * mp + 2) / 5 + 1
        val m = mp + if (mp < 10) 3 else -9
        y += if (m <= 2) 1 else 0
        return Triple(y.toInt(), m.toInt(), d.toInt())
    }

    fun splitDateTime(value: String): Pair<String, String> {
        val digits = digitsOnly(value, DateFieldKind.DATE_TIME)
        val date = formatFromDigits(digits.take(8), DateFieldKind.DATE)
        val time = formatFromDigits(digits.drop(8), DateFieldKind.TIME)
        return date to time
    }

    fun joinDateTime(date: String, time: String): String {
        val dd = digitsOnly(date, DateFieldKind.DATE)
        val tt = digitsOnly(time, DateFieldKind.TIME)
        return formatFromDigits(dd + tt, DateFieldKind.DATE_TIME)
    }
}

private class DateMaskVisualTransformation(
    private val kind: DateFieldKind
) : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val digits = text.text.filter { it.isDigit() }.take(kind.maxDigits)
        val formatted = DateInputMask.formatFromDigits(digits, kind)
        val offsetMapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                val o = offset.coerceIn(0, digits.length)
                return (o + DateInputMask.separatorBeforeDigit(kind, o))
                    .coerceIn(0, formatted.length)
            }

            override fun transformedToOriginal(offset: Int): Int {
                val t = offset.coerceIn(0, formatted.length)
                var digitCount = 0
                for (i in 0 until t) {
                    if (formatted[i].isDigit()) digitCount++
                }
                return digitCount.coerceIn(0, digits.length)
            }
        }
        return TransformedText(AnnotatedString(formatted), offsetMapping)
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AppDateTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    kind: DateFieldKind = DateFieldKind.DATE,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    isError: Boolean = false,
    supportingText: @Composable (() -> Unit)? = null,
    singleLine: Boolean = true,
    colors: TextFieldColors = FormFieldDefaults.textFieldColors()
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        if (kind == DateFieldKind.DATE_TIME && !readOnly) {
            // Data e hora em campos separados — bem mais fácil no celular.
            val (datePart, timePart) = DateInputMask.splitDateTime(value)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.Top
            ) {
                DateDigitsField(
                    value = datePart,
                    onValueChange = { d -> onValueChange(DateInputMask.joinDateTime(d, timePart)) },
                    label = "$label (data)",
                    kind = DateFieldKind.DATE,
                    enabled = enabled,
                    isError = isError,
                    modifier = Modifier.weight(1.35f),
                    colors = colors,
                    showQuickFill = false
                )
                DateDigitsField(
                    value = timePart,
                    onValueChange = { t -> onValueChange(DateInputMask.joinDateTime(datePart, t)) },
                    label = "Hora",
                    kind = DateFieldKind.TIME,
                    enabled = enabled,
                    isError = isError,
                    modifier = Modifier.weight(1f),
                    colors = colors,
                    showQuickFill = false
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    "Digite só números; a máscara completa sozinha.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                if (enabled) {
                    TextButton(onClick = { onValueChange(DateInputMask.now(DateFieldKind.DATE_TIME)) }) {
                        Text(DateInputMask.quickLabel(DateFieldKind.DATE_TIME))
                    }
                }
            }
            if (supportingText != null) supportingText()
        } else {
            DateDigitsField(
                value = value,
                onValueChange = onValueChange,
                label = label,
                kind = kind,
                enabled = enabled,
                readOnly = readOnly,
                isError = isError,
                supportingText = supportingText,
                singleLine = singleLine,
                colors = colors,
                showQuickFill = !readOnly && enabled
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DateDigitsField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    kind: DateFieldKind,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    isError: Boolean = false,
    supportingText: @Composable (() -> Unit)? = null,
    singleLine: Boolean = true,
    colors: TextFieldColors = FormFieldDefaults.textFieldColors(),
    showQuickFill: Boolean = true
) {
    val digits = DateInputMask.digitsOnly(value, kind)
    val keyboard = LocalSoftwareKeyboardController.current
    var pickerAberto by remember { mutableStateOf(false) }
    val podeAbrirCalendario = enabled && !readOnly
    Column(modifier = modifier.fillMaxWidth()) {
        AppOutlinedTextField(
            value = digits,
            onValueChange = { typed ->
                if (readOnly) return@AppOutlinedTextField
                val next = DateInputMask.constrainDigits(
                    DateInputMask.digitsOnly(typed, kind),
                    kind
                )
                onValueChange(DateInputMask.formatFromDigits(next, kind))
            },
            label = { Text(label) },
            placeholder = { Text(kind.placeholder) },
            modifier = Modifier.fillMaxWidth(),
            enabled = enabled,
            readOnly = readOnly,
            isError = isError,
            supportingText = supportingText,
            singleLine = singleLine,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            visualTransformation = DateMaskVisualTransformation(kind),
            interactionSource = remember { MutableInteractionSource() },
            colors = colors
        )
        if (podeAbrirCalendario || showQuickFill) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                if (podeAbrirCalendario) {
                    TextButton(
                        onClick = {
                            keyboard?.hide()
                            pickerAberto = true
                        }
                    ) {
                        Text(DateInputMask.pickerLabel(kind))
                    }
                }
                if (showQuickFill) {
                    TextButton(onClick = { onValueChange(DateInputMask.now(kind)) }) {
                        Text(DateInputMask.quickLabel(kind))
                    }
                }
            }
        }
        if (pickerAberto) {
            DateOrTimePickerDialog(
                kind = kind,
                currentValue = value,
                onDismiss = { pickerAberto = false },
                onConfirm = { escolhido ->
                    onValueChange(escolhido)
                    pickerAberto = false
                }
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DateOrTimePickerDialog(
    kind: DateFieldKind,
    currentValue: String,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit
) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnBackPress = true,
            dismissOnClickOutside = true
        )
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.45f))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onDismiss
                    )
            )
            Card(
                modifier = Modifier
                    .align(Alignment.Center)
                    .padding(horizontal = 16.dp, vertical = 24.dp)
                    .widthIn(max = 420.dp)
                    .fillMaxWidth(),
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surface
                ), border = PortalCores.BordaCartao,
                elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
            ) {
                Column(modifier = Modifier.padding(bottom = 8.dp)) {
                    Text(
                        text = if (kind == DateFieldKind.TIME) "Escolha a hora" else "Escolha a data",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.padding(start = 20.dp, top = 16.dp, end = 20.dp)
                    )
                    if (kind == DateFieldKind.TIME) {
                        val (hour, minute) = DateInputMask.parseHourMinute(currentValue)
                        val timeState = rememberTimePickerState(
                            initialHour = hour,
                            initialMinute = minute,
                            is24Hour = true
                        )
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = 12.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            TimePicker(state = timeState)
                        }
                        PickerDialogButtons(
                            onDismiss = onDismiss,
                            onConfirm = {
                                onConfirm(
                                    DateInputMask.formatHourMinute(timeState.hour, timeState.minute)
                                )
                            }
                        )
                    } else {
                        val initial = DateInputMask.toUtcMillis(currentValue, kind)
                            ?: DateInputMask.todayUtcMillis()
                        val dateState = rememberDatePickerState(
                            initialSelectedDateMillis = initial,
                            initialDisplayedMonthMillis = initial,
                            yearRange = DateInputMask.yearRange(kind)
                        )
                        DatePicker(
                            state = dateState,
                            title = null,
                            headline = null,
                            showModeToggle = false,
                            modifier = Modifier.fillMaxWidth()
                        )
                        PickerDialogButtons(
                            onDismiss = onDismiss,
                            onConfirm = {
                                val ms = dateState.selectedDateMillis ?: initial
                                onConfirm(DateInputMask.formatFromUtcMillis(ms, kind))
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PickerDialogButtons(
    onDismiss: () -> Unit,
    onConfirm: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically
    ) {
        TextButton(onClick = onDismiss) { Text("Cancelar") }
        TextButton(onClick = onConfirm) { Text("OK") }
        Spacer(modifier = Modifier.height(4.dp))
    }
}
